const DEFAULT_ITEMS_PER_PAGE = { value: 300, required: true };

export default class Collection<T extends ModelConstructor> {
	protected _modelClass: T;
	protected _models: InstanceType<T>[] = [];
	protected _translations?: RequestTranslations;
	protected _sorts: (Record<string, SortWay>)[] = [];
	protected _requestReportStore?: RequestTracer;
	private _resolved = new Map<string, Set<id>>();

	protected _filters: { [x: string]: { value: unknown; required: boolean; } } & {
		itemsPerPage?: { value: unknown; required: boolean; };
	} = {
		itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
	};

	protected _headers: ModelHeaders = {};

	constructor(modelClass: T, options?: {
		requestReportStore?: RequestTracer;
	}) {
		this._modelClass = modelClass;
		this._requestReportStore = options?.requestReportStore;
	}

	async list() {
		const model = new this._modelClass({}, { requestReportStore: this._requestReportStore });
		let isMissingFilter = false;

		const filters = Object.entries(this._filters).reduce<Record<string, string | string[]>>(
			(acc, [name, filter]) => {
				const value = filter.value;

				const hasValue =
					value !== ''
					&& value !== undefined
					&& value !== null
					&& (!Array.isArray(value) || value.length > 0);

				if (hasValue) {
					if (Array.isArray(value)) {
						acc[name] = value.map(v => v.toString());
					} else {
						acc[name] = value.toString();
					}
				} else if (filter.required) {
					isMissingFilter = true;
				}

				return acc;
			},
			{},
		);

		if (isMissingFilter) {
			return { collection: this, response: { data: [], status: 0, headers: new Headers() } };
		}

		const response = await model.connector.list(model.fullPath, {
			filters,
			locales: this._translations,
			sorts: this._sorts,
			headers: this._headers,
			requestReportStore: this._requestReportStore,
		});

		const data = response.data as Record<string, unknown>[];

		const models = data
			.map(attrs => (new this._modelClass(attrs, { requestReportStore: this._requestReportStore })) as InstanceType<T>)
			.map(m => m.setTranslations(this._translations));

		this.setModels(models);

		return { collection: this, response };
	}

	setModels(models: InstanceType<T>[]) {
		this._models = models;
		return this;
	}

	get models() {
		return this._models;
	}

	map = <R>(iterator: (model: InstanceType<T>, index: number) => R): R[] => this._models.map(iterator);
	find = (iterator: (model: InstanceType<T>, index: number) => boolean): InstanceType<T> | undefined => this._models.find(iterator);
	filter = (iterator: (model: InstanceType<T>, index: number) => boolean): InstanceType<T>[] => this._models.filter(iterator);

	setTranslations(translations?: RequestTranslations) {
		this._translations = translations;
		return this;
	}

	setFilter<FilterName extends keyof ModelFilters<T>>(
		name: FilterName,
		value: ModelFilters<T>[FilterName],
		required = true,
	) {
		// Déduplication si c'est un tableau
		let finalValue = value;
		if (Array.isArray(value)) {
			finalValue = Array.from(new Set(value)) as typeof value;
		}

		this._filters[name as string] = { value: finalValue, required }
		return this;
	}

	setFilters(filters: Partial<ModelFilters<T>> = {}, required = true) {
		Object.entries(filters).forEach(([name, value]) => this.setFilter(name, value, required));
		return this;
	}

	setHeader<HeaderName extends keyof ModelHeaders>(
		name: HeaderName,
		value: ModelHeaders[HeaderName],
	) {
		this._headers[name] = value;
		return this;
	}

	setSort<SortName extends keyof ModelSorts<T>>(
		name?: SortName,
		way: SortWay = 'asc',
	) {
		this._sorts = name ? [{ [name]: way }] : [];
		return this;
	}

	get first() {
		return this._models.at(0);
	}

	get last() {
		return this._models.at(-1);
	}

	filterBy<PROPERTY extends ModelPropKeys<T>>(
		propertyName: PROPERTY,
		value?: InstanceType<T>[PROPERTY],
		inverted = false,
	) {
		return this.models.filter(model => inverted ? (model[propertyName] != value) : (model[propertyName] == value));
	}

	findBy<PROPERTY extends ModelPropKeys<T>>(propertyName: PROPERTY, value: InstanceType<T>[PROPERTY]) {
		return this.models.find(model => model[propertyName] == value);
	}

	getById(id?: id) {
		return id ? this.findBy('id' as never, id as never) : undefined;
	}

	getPropertyValues<N extends ModelPropKeys<T>>(
		propertyName: N,
	): NonNullable<InstanceType<T>[N]>[] {
		const values = this.models
			.map(model => model[propertyName])
			.filter((v): v is NonNullable<InstanceType<T>[N]> => v !== undefined);

		// Retirer les doublons
		return [...new Set(values)];
	}

	get ids(): id[] {
		return this.getPropertyValues('id' as never);
	}

	async fetchResolvable<
		// --- Niveau 1 ---
		N1 extends ModelPropKeys<T> & keyof InstanceOf<T>,
		Obj1 extends InstanceOf<T>[N1],
		// --- Niveau 2 ---
		N2 extends ModelPropKeys<Obj1> & keyof Obj1,
		Obj2 extends Obj1[N2],
		// --- Niveau 3 ---
		N3 extends ModelPropKeys<Obj2> & keyof Obj2,
		Obj3 extends Obj2[N3],
		// --- Niveau 4 ---
		N4 extends ModelPropKeys<Obj3> & keyof Obj3,
		Obj4 extends Obj3[N4],
		// --- Niveau 5 ---
		N5 extends keyof Obj4
	>(
		name1: N1,
		name2?: N2,
		name3?: N3,
		name4?: N4,
		name5?: N5,
	) {
		const responses = [];
		const collections: Collection<ModelConstructor>[] = [];

		let currentModels: any[] = this.models;
		const resolvableNames = [name1, name2, name3, name4, name5] as const;

		// --- cache initialisation ---
		if (!this._resolved) {
			this._resolved = new Map<string, Set<id>>();
		}

		const pathParts: string[] = [];

		for (const resolvableName of resolvableNames) {
			if (!resolvableName) continue;

			pathParts.push(resolvableName as string);
			const pathKey = pathParts.join('.');

			const modelsWithProp = currentModels.filter(
				(m: any) => m && m[resolvableName],
			);

			if (!modelsWithProp.length) continue;

			const nestedModels = modelsWithProp
				.map((m: any) => m[resolvableName])
				.filter((nested: any) => nested?.id);

			if (!nestedModels.length) continue;

			const allIds = Array.from(
				new Set(nestedModels.map((nested: any) => nested.id as id)),
			);

			// --- récupération du cache pour ce chemin ---
			let resolvedIds = this._resolved.get(pathKey);
			if (!resolvedIds) {
				resolvedIds = new Set<id>();
				this._resolved.set(pathKey, resolvedIds);
			}

			// --- ids réellement à fetch ---
			const idsToFetch = allIds.filter((id) => !resolvedIds!.has(id));

			if (idsToFetch.length) {
				const firstNested: any = nestedModels[0];
				const ModelClass = firstNested.constructor as ModelConstructor;

				const { collection, response } = await new Collection(ModelClass, {
					requestReportStore: this._requestReportStore,
				})
					.setFilter('id', idsToFetch)
					.setTranslations(this._translations)
					.list();

				responses.push(response);
				collections.push(collection);

				// Hydratation
				nestedModels.forEach((nested: any) => {
					if (!idsToFetch.includes(nested.id)) return;

					const match = collection.getById(nested.id as id);
					if (!match) return;

					nested.setAttributes(match.attributes);

					resolvedIds!.add(nested.id);
				});
			}

			// descendre d'un niveau
			currentModels = nestedModels;
		}

		return { collection: this, responses, collections };
	}

	setItemsPerPage(value?: number) {
		this._filters.itemsPerPage = value ? { value, required: true } : DEFAULT_ITEMS_PER_PAGE;
		return this;
	}
}
